import FileRequestModal from "@/containers/Parlor/ParlorDetailContainer/components/Modal/FileRequestModal";
import NotLoginFileRequestModal from "@/components/Modal/NotLoginFileRequestModal";
import ExperimentFileRequestModal from "@/components/Modal/ExperimentFileRequestModal";
import { Box, Flex } from "@chakra-ui/react";
import { css } from "@emotion/react";
import { useOverlay } from "@toss/use-overlay";
import {
  ColorIcon,
  colors,
  customEvent,
  EventProps,
  getCdnImageSrc,
  HStack,
  Image,
} from "goi_common";
import { useRouter } from "next/router";
import { ReactNode, useMemo } from "react";

function StepGuideFooter({
  type = "DEFAULT",
  text = (
    <>
      장례가 처음이신가요? 합리적인 장례,
      <br />
      장례식장과 상담하기 전 반드시 알아보세요.
    </>
  ),
  event = {
    newGtm: {
      path: "parlor_detail",
      trigger_and_target: "step_guide_fixed_button",
    },
  },
  src = "/step-guide/initiation",
  size = "44px",
  maxWidth = "480px",
  bottom = "80px",
  right = "",
  iconMarginLeft,
  transform = "",
}: {
  type?: string;
  text?: ReactNode;
  event?: EventProps;
  src?: string;
  size?: string;
  maxWidth?: string;
  bottom?: string;
  right?: string;
  iconMarginLeft?: string;
  transform?: string;
}) {
  const overlay = useOverlay();
  const router = useRouter();
  const typeToStyle = useMemo(() => {
    if (type === "DEFAULT") {
      return {
        background: colors.white,
        color: colors.gray800,
      };
    } else if (type === "DARK") {
      return {
        background: colors.gray800,
        color: colors.white,
      };
    } else {
      return {
        background: colors.white,
        color: colors.gray800,
      };
    }
  }, [type]);

  return (
    <Flex alignItems="center" width="100%" flexDirection="column">
      <Box
        css={{
          width: "100%",
          maxWidth: maxWidth || "480px",
          margin: 0,
          position: "fixed",
          bottom: bottom || 80,
          // bottom 이 CSS 변수로 움직이는 화면(하단 고정 CTA 가 뜨는 지면)에서 툭 튀지 않게 한다.
          // 값이 바뀌지 않는 화면에서는 아무 일도 일어나지 않는다
          transition: "bottom 0.25s ease-out",
          right: right || 10,
          zIndex: 100,
          display: "flex",
          justifyContent: "end",
          transform: transform,
        }}
      >
        <HStack
          as="button"
          onClick={() => {
            customEvent(event);

            overlay.open(({ isOpen, close: onClose }) => {
              const path =
                router.pathname.includes("facilties") || router.pathname.includes("memori")
                  ? "facility-detail"
                  : "home";
              // /service/[type]는 실험 #302에서 B 변형 정착(NotLoginFileRequestModal 고정).
              // 시설류(parlors/region, facilities, crematorium)는 실험 #307 A/B 적용.
              // 그 외(parlor 목록/가격, promotion 등)는 기본 FileRequestModal 유지.
              if (router.pathname === "/service/[type]") {
                return <NotLoginFileRequestModal path={path} isOpen={isOpen} onClose={onClose} />;
              }
              // 실험 #307 (시설류): parlor 목록 / 가격 페이지.
              if (router.pathname === "/parlor") {
                return (
                  <ExperimentFileRequestModal
                    expPage="parlor_list"
                    path={path}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                );
              }
              if (router.pathname === "/parlor/[id]/prices/[type]") {
                return (
                  <ExperimentFileRequestModal
                    expPage="parlor_prices"
                    path={path}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                );
              }
              // 실험 #307 (시설류): region 목록 / crematorium / sea-burial 상세에서 A/B 적용.
              // path는 StepGuideFooter 계산값(home) 유지하고, 페이지 구분은 exp_page로 한다.
              if (
                router.pathname === "/parlors/region/[code]" ||
                router.pathname === "/parlors/region/[code]/[sigungu]"
              ) {
                return (
                  <ExperimentFileRequestModal
                    expPage="region"
                    path={path}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                );
              }
              if (router.pathname === "/crematorium/[id]") {
                return (
                  <ExperimentFileRequestModal
                    expPage="crematorium"
                    path={path}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                );
              }
              if (router.pathname === "/facilities/sea-burial/[id]") {
                return (
                  <ExperimentFileRequestModal
                    expPage="seaburial"
                    path={path}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                );
              }
              // 수목장 / 납골당 상세
              if (router.pathname === "/facilities/region/[type]/[code]") {
                return (
                  <ExperimentFileRequestModal
                    expPage="facilities_region"
                    path={path}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                );
              }
              return <FileRequestModal path={path} isOpen={isOpen} onClose={onClose} />;
            });
          }}
          css={css`
            // margin-right: 10px;
            background: ${typeToStyle.background};
            box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 12px rgba(113, 120, 125, 0.4);
            border-radius: 26px;
          `}
          padding="4px 10px 4px 4px"
          alignItems="center"
          justifyContent="center"
          gap={8}
        >
          <Image
            src={getCdnImageSrc(`platform/home/match-request-version/circle-guidebook.png`)}
            alt="step-guide"
            width={size}
            height={size}
          />
          <Box className="subtitle_14_b" textAlign="left" color={typeToStyle.color}>
            장례비용 예상 견적표,
            <br />
            임종준비 체크리스트 받아보기
          </Box>
          <ColorIcon
            name="icon-arrow-right-circle-mono"
            size={36}
            color={typeToStyle.color}
            css={{ marginLeft: iconMarginLeft || "0px" }}
          />
        </HStack>
      </Box>
    </Flex>
  );
}

export default StepGuideFooter;
